// Screens (SCREENS-S1; docs/design/2026-10-28-screens-stack-contract.md, decision 2026-10-28
// §7–§8): the company's browser tabs and desktops on its ClawPanel stack, leased per run by the
// broker (services/api/src/domain/screens), watched and taken over by people in the Screens tab.
//
//   people   `screens.*` (the tab: live grid, Waiting, Needs you; Watch, Take over, Hand back,
//            admins bump and stop) and `browserAccounts.*` (a person's logins and their grants)
//   agents   `agentScreens.*` (run token): lease browser|desktop for a task and an account, act,
//            release — the MCP tools `screens_lease`, `screens_act`, `screens_release` call these.
//            An agent never sees a URL, a credential, a container or a machine name.
//
// The `agents` add-on: screens exist for agents.
import { z } from "zod";
import {
  BrowserAccountGrantLevel,
  BrowserAccountGrantSubject,
  BrowserAccountKind,
  BrowserAccountStatus,
  BrowserLiveMode,
  ScreenAskState,
  ScreenLeaseHolder,
  ScreenLeaseKind,
  ScreenLeaseOrigin,
  ScreenLeaseState,
  ScreenNeedsYouWhy,
  ScreenStackPlacement,
  ScreenStackState,
} from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { ScreenStackCapacity, ScreenStackHealth } from "../screens-rows";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const agents = { feature: "agents", tag: "screens" } as const;

/** `instagram/valentina`: site/name, lowercase. */
export const BrowserAccountHandle = z
  .string()
  .max(81)
  .regex(/^[a-z0-9][a-z0-9._-]{0,39}\/[a-z0-9][a-z0-9._-]{0,39}$/)
  .describe("site/name, lowercase, e.g. instagram/valentina");

const Note = z.string().trim().min(1).max(280);

export const ScreenStackView = z
  .object({
    name: z.string(),
    state: ScreenStackState,
    placement: ScreenStackPlacement,
    browser_live: BrowserLiveMode,
    health: ScreenStackHealth,
    capacity: ScreenStackCapacity,
    last_seen_at: Timestamp,
  })
  .meta({ id: "ScreenStackView" });

const Who = z.object({ membership_id: Uuid, name: z.string() });

/**
 * A lease's state as people see it: the stored states plus `starting` (a queued lease the stack is
 * being asked to start right now, so not "waiting for a free slot").
 */
export const ScreenLeaseViewState = z
  .enum([...ScreenLeaseState.options, "starting"])
  .meta({ id: "ScreenLeaseViewState" });

export const ScreenLeaseView = z
  .object({
    id: Uuid,
    kind: ScreenLeaseKind,
    state: ScreenLeaseViewState,
    holder: Who.extend({ kind: ScreenLeaseHolder }),
    task: z.object({ id: Uuid, title: z.string() }).nullable(),
    account: z.object({ id: Uuid, handle: z.string(), label: z.string() }).nullable(),
    origin: ScreenLeaseOrigin,
    priority: z.int(),
    reason: z.string().nullable(),
    /** Page title or app name (no page content). */
    title: z.string().nullable(),
    queued_at: Timestamp,
    started_at: Timestamp.nullable(),
    last_activity_at: Timestamp.nullable(),
    /** 1-based place in the Waiting list (queued only). */
    position: z.int().min(1).nullable(),
    controller: Who.nullable(),
    needs_you: z
      .object({
        why: ScreenNeedsYouWhy,
        note: z.string().nullable(),
        /** PERSISTENT-SCREENS: the open ask (its card) and when it expires (the countdown). */
        ask_id: Uuid.nullable().optional(),
        expires_at: Timestamp.nullable().optional(),
      })
      .nullable(),
    hand_back_note: z.string().nullable(),
    /** A ClawPanel operator has control of it now (the stack reports it). */
    operator_control: z.boolean(),
    /** ClawPanel operators watching it now (controllers included). */
    operator_viewing: z.int().min(0),
    live_mode: BrowserLiveMode,
    /** What the caller may do here. */
    can: z.object({
      watch: z.boolean(),
      control: z.boolean(),
      bump: z.boolean(),
      stop: z.boolean(),
    }),
  })
  .meta({ id: "ScreenLeaseView" });
export type ScreenLeaseView = z.infer<typeof ScreenLeaseView>;

export const ScreensOverview = z
  .object({
    /** Null: the company has no stack (ClawPanel has not registered one). */
    stack: ScreenStackView.nullable(),
    live: z.array(ScreenLeaseView),
    waiting: z.array(ScreenLeaseView),
    /** Live leases waiting on a person the caller can help (owner of the account, or an admin). */
    needs_you: z.array(ScreenLeaseView),
    /**
     * Screens the ClawPanel operator started from its own page: they use the same capacity,
     * are read-only here and are never ended by CompanyOS.
     */
    operator: z.array(
      z.object({
        lease_id: z.string(),
        kind: ScreenLeaseKind,
        name: z.string(),
        started_at: z.string().nullable(),
        operator_control: z.boolean(),
        operator_viewing: z.int().min(0),
      }),
    ),
    /** False when the stack did not answer the reconciliation just now. */
    stack_reachable: z.boolean(),
    can_bump: z.boolean(),
  })
  .meta({ id: "ScreensOverview" });

export const ScreenLeaseOpen = z
  .strictObject({
    kind: ScreenLeaseKind,
    /** Browser only: open on this login (the owner's own, to log in or look). */
    account_id: Uuid.optional(),
    reason: Note.optional(),
  })
  .meta({ id: "ScreenLeaseOpen" });

/** The viewer's address bar on a browser screen (the person driving it). */
export const ScreenBrowse = z
  .strictObject({
    action: z.enum(["location", "navigate", "back", "reload"]),
    /** navigate: http(s) only; a bare host or search words are made a URL by the web app. */
    url: z
      .url({ protocol: /^https?$/ })
      .max(2048)
      .optional(),
  })
  .meta({ id: "ScreenBrowse" });
export const ScreenBrowseResult = z
  .object({
    /** The page's address after the action (null: the stack did not say). */
    url: z.string().nullable(),
  })
  .meta({ id: "ScreenBrowseResult" });

export const ScreenTicketRequest = z
  .strictObject({ role: z.enum(["watch", "control"]) })
  .meta({ id: "ScreenTicketRequest" });
export const ScreenTicket = z
  .object({
    /** The relay (`screens.live`) with a 60 s ticket; open it as a WebSocket. */
    url: z.string(),
    expires_at: Timestamp,
    role: z.enum(["watch", "control"]),
    live_mode: BrowserLiveMode,
  })
  .meta({ id: "ScreenTicket" });

export const ScreenHandBack = z
  .strictObject({ note: Note.optional() })
  .meta({ id: "ScreenHandBack" });

export const ScreenFrame = z
  .object({
    /** A PNG screenshot, base64 (screenshot mode, or a thumbnail). */
    image_png_base64: z.string(),
    at: Timestamp,
  })
  .meta({ id: "ScreenFrame" });

export const ScreenLiveQuery = z.object({ ticket: z.string().min(1).max(2048) });

// Accounts ----------------------------------------------------------------------------------------

export const BrowserAccountGrantView = z
  .object({
    id: Uuid,
    subject_kind: BrowserAccountGrantSubject,
    subject_id: Uuid,
    subject_name: z.string(),
    level: BrowserAccountGrantLevel,
    /** SAVED-LOGIN-SHARING: the owner or admin who shared it (Connections › Accounts shows it). */
    granted_by: Who.nullable().optional(),
    created_at: Timestamp,
  })
  .meta({ id: "BrowserAccountGrantView" });

/** SAVED-LOGIN-SHARING: who drives a login's session now (one at a time) and how many wait. */
export const BrowserAccountInUse = z
  .object({
    membership_id: Uuid,
    name: z.string(),
    kind: z.enum(["de", "ag", "person"]),
    since: Timestamp.nullable(),
  })
  .meta({ id: "BrowserAccountInUse" });

/** "Client posts, Mon–Wed": when to use a login; agents read it to pick between several. */
const UseFor = z.string().trim().max(280);

/**
 * A saved profile (PERSISTENT-SCREENS): a browser's session for one site, or a desktop's home
 * volume. The session itself stays on the company's stack, encrypted at rest; CompanyOS keeps
 * only this row (never a password, never a cookie).
 */
export const BrowserAccountView = z
  .object({
    id: Uuid,
    handle: z.string(),
    kind: BrowserAccountKind,
    /** The site (browser); null for a desktop home. */
    origin: z.string().nullable(),
    label: z.string(),
    /** SAVED-LOGIN-SHARING: when to use it (null: not said). */
    use_for: z.string().nullable().optional(),
    status: BrowserAccountStatus,
    owner: Who,
    /** SAVED-LOGIN-SHARING: whoever drives its session now; null when it is free. */
    in_use_by: BrowserAccountInUse.nullable().optional(),
    /** Screens waiting for this login (they start when it is free). */
    waiting: z.int().min(0).optional(),
    /** Who added it (a person, or the agent that saved it on first login). */
    created_by: Who.extend({ kind: z.enum(["person", "agent", "system"]) }).nullable(),
    grants: z.array(BrowserAccountGrantView),
    last_used_at: Timestamp.nullable(),
    created_at: Timestamp,
    /** The owner or an admin: grant, revoke, log in. */
    can_manage: z.boolean(),
  })
  .meta({ id: "BrowserAccountView" });
export type BrowserAccountView = z.infer<typeof BrowserAccountView>;

export const BrowserAccountList = z
  .object({ data: z.array(BrowserAccountView) })
  .meta({ id: "BrowserAccountList" });

export const BrowserAccountCreate = z
  .strictObject({
    handle: BrowserAccountHandle,
    /** Default `browser`; a `desktop` profile keeps a desktop's home (no site). */
    kind: BrowserAccountKind.optional(),
    /** The site (browser only). */
    origin: z
      .url()
      .max(512)
      .refine((u) => u.startsWith("https://"), "https only")
      .optional(),
    label: z.string().trim().min(1).max(80),
    use_for: UseFor.optional(),
  })
  .refine((b) => (b.kind === "desktop") === (b.origin === undefined), {
    message: "a browser profile needs its site; a desktop profile has none",
    path: ["origin"],
  })
  .meta({ id: "BrowserAccountCreate" });

export const BrowserAccountGrantCreate = z
  .strictObject({
    subject_kind: BrowserAccountGrantSubject,
    subject_id: Uuid,
    level: BrowserAccountGrantLevel,
  })
  .meta({ id: "BrowserAccountGrantCreate" });

/** SAVED-LOGIN-SHARING: rename a login or say when to use it (owner or admin). */
export const BrowserAccountUpdate = z
  .strictObject({
    label: z.string().trim().min(1).max(80).optional(),
    /** Empty or null clears it. */
    use_for: UseFor.nullable().optional(),
  })
  .refine((b) => b.label !== undefined || b.use_for !== undefined, {
    message: "nothing to change",
  })
  .meta({ id: "BrowserAccountUpdate" });

const GrantParams = z.object({ co: Uuid, id: Uuid, grant: Uuid });

// Agents ------------------------------------------------------------------------------------------

/** `instagram.com` or `https://www.instagram.com/…`: the site a saved profile is for. */
export const ScreenSite = z
  .string()
  .trim()
  .min(3)
  .max(512)
  .describe("a site's host or https URL, e.g. instagram.com");

export const AgentScreenLeaseRequest = z
  .strictObject({
    kind: ScreenLeaseKind,
    /**
     * A saved profile the caller's DE or seat is granted, by handle (`instagram/brand`) or, from
     * SAVED-LOGIN-SHARING, by its name as `screens_profiles` lists it; omitted = no login.
     */
    account: z.string().trim().min(1).max(81).optional(),
    /**
     * PERSISTENT-SCREENS (browser): the site to work on. The broker opens the caller's saved
     * profile for it (granted to it); when the company has none for that site it saves a new one
     * (owned by the person the work is for) so a login there is kept for next time.
     */
    site: ScreenSite.optional(),
    /** A CompanyOS task id; omitted (SCREENS-POOL-FIX) = the run's own task. */
    task: Uuid.optional(),
    reason: Note.optional(),
  })
  .meta({ id: "AgentScreenLeaseRequest" });

export const AgentScreenLease = z
  .object({
    lease_id: Uuid,
    state: ScreenLeaseState,
    position: z.int().min(1).nullable(),
    account: z.string().nullable(),
    /** The saved profile's state: live (was signed in last time), needs_you (never yet). */
    account_status: BrowserAccountStatus.nullable().optional(),
    /** SAVED-LOGIN-SHARING: the login's name, and who drives it while this lease waits. */
    account_label: z.string().nullable().optional(),
    in_use_by: z.string().nullable().optional(),
    /** What to do next (check the saved login first; a person still has the screen). */
    hint: z.string().nullable().optional(),
  })
  .meta({ id: "AgentScreenLease" });

/** A saved profile an agent may use (MCP `screens_profiles`). */
export const AgentScreenProfile = z
  .object({
    handle: z.string(),
    kind: BrowserAccountKind,
    site: z.string().nullable(),
    label: z.string(),
    /** SAVED-LOGIN-SHARING: when to use it, in the owner's words (pick by this or by label). */
    use_for: z.string().nullable().optional(),
    /** The person asked to log in again when its session expires. */
    owner: z.string().optional(),
    status: BrowserAccountStatus,
    level: BrowserAccountGrantLevel,
    /** Who drives it now (one at a time; a lease on it waits until it is free). */
    in_use_by: z.string().nullable().optional(),
    waiting: z.int().min(0).optional(),
    last_used_at: Timestamp.nullable(),
  })
  .meta({ id: "AgentScreenProfile" });
export const AgentScreenProfileList = z
  .object({ data: z.array(AgentScreenProfile) })
  .meta({ id: "AgentScreenProfileList" });

/** Extend a needs-you ask's wait (minutes; default 15). */
export const ScreenAskExtend = z
  .strictObject({ minutes: z.int().min(5).max(60).optional() })
  .meta({ id: "ScreenAskExtend" });

const Str = (max: number) => z.string().min(1).max(max);
const Ref = z
  .string()
  .max(32)
  .regex(/^[a-zA-Z0-9_-]{1,32}$/);
const Px = z.int().min(0).max(10_000);
const WebUrl = z
  .url({ protocol: /^https?$/, error: "only http/https URLs can be opened" })
  .max(2048);
export const AgentScreenAct = z
  .discriminatedUnion("action", [
    // browser
    // Camofox opens http(s) only (view-source:, file:, javascript: are refused there): say so here.
    z.strictObject({ action: z.literal("open"), url: WebUrl }),
    z.strictObject({ action: z.literal("snapshot"), tab: Str(64).optional() }),
    // An agent's screenshot is downscaled (≤ 768 px on the long side, JPEG); full: the original.
    z.strictObject({
      action: z.literal("screenshot"),
      tab: Str(64).optional(),
      full: z.boolean().optional(),
    }),
    z.strictObject({
      action: z.literal("click"),
      ref: Ref.optional(),
      tab: Str(64).optional(),
      x: Px.optional(),
      y: Px.optional(),
      button: z.enum(["left", "right"]).optional(),
      double: z.boolean().optional(),
    }),
    z.strictObject({
      action: z.literal("type"),
      text: z.string().max(4000),
      ref: Ref.optional(),
      tab: Str(64).optional(),
      submit: z.boolean().optional(),
    }),
    z.strictObject({ action: z.literal("press"), key: Str(32), tab: Str(64).optional() }),
    z.strictObject({ action: z.literal("key"), keys: z.array(Str(32)).min(1).max(5) }),
    z.strictObject({
      action: z.literal("scroll"),
      direction: z.enum(["up", "down", "left", "right"]).optional(),
      x: Px.optional(),
      y: Px.optional(),
      dy: z.int().min(-50).max(50).optional(),
      tab: Str(64).optional(),
    }),
    z.strictObject({
      action: z.literal("navigate"),
      url: WebUrl,
      tab: Str(64).optional(),
    }),
    z.strictObject({ action: z.literal("back"), tab: Str(64).optional() }),
    z.strictObject({ action: z.literal("close_tab"), tab: Str(64) }),
    // desktop: the screen as text (open windows, then the focused window's elements with where
    // to click them) for a model that cannot see images
    z.strictObject({ action: z.literal("read") }),
  ])
  .meta({ id: "AgentScreenAct" });
export type AgentScreenAct = z.infer<typeof AgentScreenAct>;

/** An agent hit a login wall, 2FA or a CAPTCHA and asks a person to step in. */
export const AgentScreenNeedsYou = z
  .strictObject({
    why: ScreenNeedsYouWhy,
    /** "so I can …": shown on the chat card. */
    note: Note.optional(),
    /**
     * How long the screen waits on the person (s). Never shorter than the company's wait
     * (default 30 min, `settings.screens.needs_you_wait_min`); past it the ask expires.
     */
    wait_s: z.int().min(30).max(14_400).optional(),
    /**
     * PERSISTENT-SCREENS: a login on a browser with no saved profile is lost when it closes, so
     * it is refused unless the agent means it (true: ask anyway, nothing is kept).
     */
    throwaway: z.boolean().optional(),
  })
  .meta({ id: "AgentScreenNeedsYou" });

/**
 * SCREENS-NEEDS-YOU: a needs-you ask as its chat card shows it, read live (open, then "Done by …",
 * "Not now", "Expired", or ended with its screen). Only the account's owner or an admin opens it.
 */
export const ScreenAskView = z
  .object({
    id: Uuid,
    lease_id: Uuid,
    state: ScreenAskState,
    why: ScreenNeedsYouWhy,
    reason: z.string().nullable(),
    agent: z.object({ membership_id: Uuid, name: z.string(), kind: z.enum(["de", "ag"]) }),
    account: z.object({ id: Uuid, handle: z.string(), label: z.string() }).nullable(),
    /** Who opens it besides admins (the account's owner); null = admins only. */
    owner: Who.nullable(),
    expires_at: Timestamp,
    resolved_by: Who.nullable(),
    resolved_at: Timestamp.nullable(),
    created_at: Timestamp,
    /** The caller may open the screen (take over, log in, hand back) or say not now. */
    can_open: z.boolean(),
    /** The screen's state now (taken over while a person logs in). */
    lease_state: ScreenLeaseViewState,
    /** SCREEN-HANDOFF: who is in control of the screen now, while the ask is open. */
    controller: Who.nullable(),
    /** The agent can still be woken (retired agents are not). */
    agent_active: z.boolean(),
    /** "Start a new screen" was asked for (once per ask). */
    restart_requested_at: Timestamp.nullable(),
    /** The caller may ask the agent for a new screen (expired, ended or declined, agent active). */
    can_restart: z.boolean(),
    /** The card's message (the ask's own id; null before it was posted anywhere). */
    message_id: Uuid.nullable(),
    /** PERSISTENT-SCREENS: Extend presses so far, and whether the caller may extend now. */
    extensions: z.int().min(0).optional(),
    can_extend: z.boolean().optional(),
  })
  .meta({ id: "ScreenAskView" });
export type ScreenAskView = z.infer<typeof ScreenAskView>;
export type AgentScreenNeedsYou = z.infer<typeof AgentScreenNeedsYou>;

export const AgentScreenActResult = z
  .object({
    state: ScreenLeaseState,
    /** Set when the agent must wait: why (taken over, queued, waiting on a person, ended). */
    wait: z.string().nullable(),
    /** The person's hand-back note, once, after a take-over. */
    note: z.string().nullable(),
    result: z
      .object({
        tab: z.string().optional(),
        url: z.string().optional(),
        text: z.string().optional(),
        image_png_base64: z.string().optional(),
        /** A desktop's screenshot for an agent: JPEG, in the desktop's screen points. */
        image_jpeg_base64: z.string().optional(),
        /** A screenshot in one line of words: the page (title, URL) or active window, cursor. */
        summary: z.string().optional(),
      })
      .nullable(),
  })
  .meta({ id: "AgentScreenActResult" });

export const AgentScreenRelease = z
  .strictObject({ note: Note.optional() })
  .meta({ id: "AgentScreenRelease" });

export const screensRoutes = {
  "screens.overview": defineRoute({
    ...agents,
    id: "screens.overview",
    method: "get",
    path: `${base}/screens`,
    summary: "The Screens tab: the company stack, live screens, the Waiting list and Needs you",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: ScreensOverview },
    errors: [],
    emits: [],
  }),
  "screens.open": defineRoute({
    ...agents,
    id: "screens.open",
    method: "post",
    path: `${base}/screens/leases`,
    summary: "Open a browser or desktop for myself (e.g. to log in to my account); queued if full",
    access: "member",
    params: CompanyParams,
    body: ScreenLeaseOpen,
    response: { status: 201, schema: ScreenLeaseView },
    errors: [
      "screens_no_stack",
      "screens_kind_unavailable",
      "screens_no_grant",
      "screens_stack_failed",
    ],
    emits: ["screen_lease.queued", "screen_lease.started"],
  }),
  "screens.ticket": defineRoute({
    ...agents,
    id: "screens.ticket",
    method: "post",
    path: `${base}/screens/leases/{id}/ticket`,
    summary: "A 60 s ticket to watch a screen, or to drive it while I have taken it over",
    access: "member",
    params: CompanyItemParams,
    body: ScreenTicketRequest,
    response: { status: 200, schema: ScreenTicket },
    errors: ["screens_lease_state", "screens_controlled"],
    emits: [],
    safe: true,
  }),
  "screens.live": defineRoute({
    ...agents,
    id: "screens.live",
    method: "get",
    path: `${base}/screens/leases/{id}/live`,
    summary: "The live view relay (WebSocket upgrade with a ticket from `screens.ticket`)",
    description:
      "With `Upgrade: websocket`, a trusted Origin and the same signed-in person the ticket was " +
      "minted for: CompanyOS opens one upstream to the stack's live proxy over the overlay and " +
      "pipes RFB both ways; a watch ticket's input (KeyEvent, PointerEvent, ClientCutText) is " +
      "dropped. Without an upgrade: 200 with the ticket's state.",
    access: "member",
    params: CompanyItemParams,
    query: ScreenLiveQuery,
    response: { status: 200, schema: z.object({ ok: z.literal(true) }) },
    errors: ["not_ready", "screens_lease_state"],
    emits: [],
  }),
  "screens.frame": defineRoute({
    ...agents,
    id: "screens.frame",
    method: "get",
    path: `${base}/screens/leases/{id}/frame`,
    summary: "The screen's current picture (the screenshot stream when VNC is not offered)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ScreenFrame },
    errors: ["screens_lease_state", "screens_stack_failed"],
    emits: [],
  }),
  "screens.browse": defineRoute({
    ...agents,
    id: "screens.browse",
    method: "post",
    path: `${base}/screens/leases/{id}/browse`,
    summary:
      "The address bar of a browser screen: where it is, go to a URL, back, reload (its driver)",
    access: "member",
    params: CompanyItemParams,
    body: ScreenBrowse,
    response: { status: 200, schema: ScreenBrowseResult },
    errors: ["screens_lease_state", "screens_controlled", "screens_stack_failed"],
    emits: [],
    // Writes no row and no event (like the ticket): it drives the browser on the stack.
    safe: true,
  }),
  "screens.takeOver": defineRoute({
    ...agents,
    id: "screens.takeOver",
    method: "post",
    path: `${base}/screens/leases/{id}/take-over`,
    summary: "Take over a screen (the account's owner or an admin): the agent pauses",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ScreenLeaseView },
    errors: ["screens_lease_state", "screens_controlled"],
    emits: ["screen_lease.taken_over"],
  }),
  "screens.handBack": defineRoute({
    ...agents,
    id: "screens.handBack",
    method: "post",
    path: `${base}/screens/leases/{id}/hand-back`,
    summary: "Hand a screen back to its agent, with an optional note it reads",
    access: "member",
    params: CompanyItemParams,
    body: ScreenHandBack,
    response: { status: 200, schema: ScreenLeaseView },
    errors: ["screens_lease_state", "screens_controlled"],
    emits: [
      "screen_lease.handed_back",
      "browser_account.status_changed",
      "screen_ask.resolved",
      "message.created",
      "wakeup.requested",
    ],
  }),
  "screens.ask": defineRoute({
    ...agents,
    id: "screens.ask",
    method: "get",
    path: `${base}/screens/asks/{id}`,
    summary: "A needs-you ask as its chat card shows it (live state, who may open it)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ScreenAskView },
    errors: [],
    emits: [],
  }),
  "screens.askNotNow": defineRoute({
    ...agents,
    id: "screens.askNotNow",
    method: "post",
    path: `${base}/screens/asks/{id}/not-now`,
    summary: "Say not now to a needs-you ask (its owner or an admin): the agent is told at once",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ScreenAskView },
    errors: ["screens_lease_state"],
    emits: ["screen_ask.resolved"],
  }),
  "screens.askRestart": defineRoute({
    ...agents,
    id: "screens.askRestart",
    method: "post",
    path: `${base}/screens/asks/{id}/restart`,
    summary:
      "Start a new screen: an expired or ended needs-you ask asks its agent to open a new screen and ask again (its person or an admin; once)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ScreenAskView },
    errors: ["screens_lease_state", "conflict"],
    emits: ["message.created", "wakeup.requested"],
  }),
  "screens.askExtend": defineRoute({
    ...agents,
    id: "screens.askExtend",
    method: "post",
    path: `${base}/screens/asks/{id}/extend`,
    summary:
      "Keep a screen waiting on a person open longer: an open needs-you ask's wait grows (its person, whoever has the screen, or an admin; capped)",
    access: "member",
    params: CompanyItemParams,
    body: ScreenAskExtend,
    response: { status: 200, schema: ScreenAskView },
    errors: ["screens_lease_state", "conflict"],
    emits: ["screen_ask.extended"],
  }),
  "screens.bump": defineRoute({
    ...agents,
    id: "screens.bump",
    method: "post",
    path: `${base}/screens/leases/{id}/bump`,
    summary: "Move a waiting screen to the front of the Waiting list (admins)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: ScreenLeaseView },
    errors: ["screens_lease_state"],
    emits: ["screen_lease.bumped"],
  }),
  "screens.stop": defineRoute({
    ...agents,
    id: "screens.stop",
    method: "post",
    path: `${base}/screens/leases/{id}/stop`,
    summary: "End a screen (admins, or the account's owner; the agent is told)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ScreenLeaseView },
    errors: ["screens_lease_state"],
    emits: [
      "screen_lease.ended",
      "screen_lease.started",
      "browser_account.status_changed",
      "screen_ask.resolved",
    ],
  }),
  "browserAccounts.list": defineRoute({
    ...agents,
    id: "browserAccounts.list",
    method: "get",
    path: `${base}/browser-accounts`,
    summary: "The company's browser logins (accounts), owners and grants",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: BrowserAccountList },
    errors: [],
    emits: [],
  }),
  "browserAccounts.create": defineRoute({
    ...agents,
    id: "browserAccounts.create",
    method: "post",
    path: `${base}/browser-accounts`,
    summary: "Add one of my logins (I own it; I log in from the Screens tab)",
    access: "member",
    params: CompanyParams,
    body: BrowserAccountCreate,
    response: { status: 201, schema: BrowserAccountView },
    errors: ["conflict"],
    emits: ["browser_account.created"],
  }),
  "browserAccounts.revoke": defineRoute({
    ...agents,
    id: "browserAccounts.revoke",
    method: "post",
    path: `${base}/browser-accounts/{id}/revoke`,
    summary: "Remove a login: its live screens end and its profile is wiped (owner or admin)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: BrowserAccountView },
    errors: [],
    emits: [
      "browser_account.revoked",
      "screen_lease.ended",
      "screen_lease.started",
      "screen_ask.resolved",
    ],
  }),
  "browserAccounts.login": defineRoute({
    ...agents,
    id: "browserAccounts.login",
    method: "post",
    path: `${base}/browser-accounts/{id}/login`,
    summary:
      "Open a browser on one of my logins to log in (the account's owner or an admin); closing it keeps the session for its agents",
    access: "member",
    params: CompanyItemParams,
    response: { status: 201, schema: ScreenLeaseView },
    errors: ["screens_no_stack", "screens_kind_unavailable", "screens_no_grant"],
    emits: ["screen_lease.queued", "screen_lease.started"],
  }),
  "browserAccounts.grant": defineRoute({
    ...agents,
    id: "browserAccounts.grant",
    method: "post",
    path: `${base}/browser-accounts/{id}/grants`,
    summary: "Lend a login to a DE or a seat: use_read, use_act or manage (owner or admin)",
    access: "member",
    params: CompanyItemParams,
    body: BrowserAccountGrantCreate,
    response: { status: 200, schema: BrowserAccountView },
    errors: ["conflict"],
    emits: ["browser_account.granted"],
  }),
  "browserAccounts.ungrant": defineRoute({
    ...agents,
    id: "browserAccounts.ungrant",
    method: "post",
    path: `${base}/browser-accounts/{id}/grants/{grant}/revoke`,
    summary: "Take a grant back (owner or admin)",
    access: "member",
    params: GrantParams,
    response: { status: 200, schema: BrowserAccountView },
    errors: [],
    emits: ["browser_account.grant_revoked"],
  }),
  "browserAccounts.update": defineRoute({
    ...agents,
    id: "browserAccounts.update",
    method: "patch",
    path: `${base}/browser-accounts/{id}`,
    summary: "Rename a login or say when to use it (owner or admin); agents read it to pick one",
    access: "member",
    params: CompanyItemParams,
    body: BrowserAccountUpdate,
    response: { status: 200, schema: BrowserAccountView },
    errors: [],
    emits: ["browser_account.updated"],
  }),
  "agentScreens.lease": defineRoute({
    ...agents,
    id: "agentScreens.lease",
    method: "post",
    path: `${base}/agent/screens/leases`,
    summary: "An agent asks for a browser or a desktop for a task (MCP `screens_lease`)",
    access: "run_token",
    params: CompanyParams,
    body: AgentScreenLeaseRequest,
    response: { status: 200, schema: AgentScreenLease },
    errors: [
      "screens_no_stack",
      "screens_kind_unavailable",
      "screens_no_grant",
      "screens_pick_account",
      "screens_account_needs_you",
      "screens_stack_failed",
      "run_not_owned",
    ],
    emits: [
      "screen_lease.queued",
      "screen_lease.started",
      "browser_account.created",
      "browser_account.granted",
    ],
  }),
  "agentScreens.profiles": defineRoute({
    ...agents,
    id: "agentScreens.profiles",
    method: "get",
    path: `${base}/agent/screens/profiles`,
    summary:
      "The saved profiles (browser logins, desktop homes) an agent may use (MCP `screens_profiles`)",
    access: "run_token",
    params: CompanyParams,
    response: { status: 200, schema: AgentScreenProfileList },
    errors: ["run_not_owned"],
    emits: [],
  }),
  "agentScreens.act": defineRoute({
    ...agents,
    id: "agentScreens.act",
    method: "post",
    path: `${base}/agent/screens/leases/{id}/act`,
    summary: "An agent acts on its leased screen (MCP `screens_act`)",
    access: "run_token",
    params: CompanyItemParams,
    body: AgentScreenAct,
    response: { status: 200, schema: AgentScreenActResult },
    errors: ["screens_lease_state", "screens_stack_failed", "screens_no_grant"],
    // Acting on the stack is not an event (the lease only records its last activity and tabs).
    // A lease the stack lost is ended by the sweep (`job:screens.sweep`), not here.
    emits: [],
    safe: true,
  }),
  "agentScreens.needsYou": defineRoute({
    ...agents,
    id: "agentScreens.needsYou",
    method: "post",
    path: `${base}/agent/screens/leases/{id}/needs-you`,
    summary: "An agent asks a person to step in (login, 2FA, CAPTCHA; MCP `screens_act` needs_you)",
    access: "run_token",
    params: CompanyItemParams,
    body: AgentScreenNeedsYou,
    response: { status: 200, schema: AgentScreenActResult },
    errors: ["screens_lease_state"],
    emits: ["screen_lease.needs_you", "browser_account.status_changed", "message.created"],
  }),
  "agentScreens.release": defineRoute({
    ...agents,
    id: "agentScreens.release",
    method: "post",
    path: `${base}/agent/screens/leases/{id}/release`,
    summary: "An agent is done with its screen (MCP `screens_release`)",
    access: "run_token",
    params: CompanyItemParams,
    body: AgentScreenRelease,
    response: { status: 200, schema: AgentScreenLease },
    errors: [],
    // A screen a person is on is kept (no ask ends here any more): PERSISTENT-SCREENS.
    emits: ["screen_lease.ended", "screen_lease.started"],
  }),
} as const;
